import type {ReactElement} from 'react';
import {useContext, useCallback} from 'react';
import {formMakerContext} from '@FormMakerReact/form-maker.context';
import {addCustomField, countCustomFieldsInRegistry} from '@FormMakerReact/lib/form-maker-fields-mutator-library';
import {MAX_CUSTOM_FIELDS} from '@FormMakerReact/lib/form-maker-fields-mutator-library';
import type {FieldMeta} from '@FormMakerReact/form-maker.types';
import {PMW_CUSTOM_INPUT} from '@FormMakerReact/form-maker.types';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text/text';
import type {AddCustomFieldButtonControlProps} from './add-custom-field-button-control.types';
import styles from './add-custom-field-button-control.module.scss';

export function AddCustomFieldButtonControl({fieldsMeta}: AddCustomFieldButtonControlProps): ReactElement | null {
  const {editor, actions} = useContext(formMakerContext);

  const handleClick = useCallback((): void => {
    if (!editor) {
      return;
    }
    const newIndex = addCustomField(editor, fieldsMeta);
    if (newIndex !== null) {
      appendCustomFieldToMeta(actions.setFieldsMeta, fieldsMeta, newIndex);
    }
  }, [editor, fieldsMeta, actions.setFieldsMeta]);

  if (countCustomFieldsInRegistry(editor, fieldsMeta) >= MAX_CUSTOM_FIELDS) {
    return null;
  }

  return (
    <button type="button" className={styles.button} onClick={handleClick}>
      <Icon icon="icon-plus" size={IconSize.SIZE_ICON_24} className={styles.buttonIcon} />
      <Text val={window.i18next.t('pmwjs_new_custom_field')} size={TextSize.MD} className={styles.buttonText} />
    </button>
  );
}

function appendCustomFieldToMeta(setFieldsMeta: (updater: (prev: FieldMeta[]) => FieldMeta[]) => void, currentFieldsMeta: FieldMeta[], newIndex: number): void {
  const label = `${window.i18next.t('pmwjs_custom_field')} ${newIndex.toString()}`;
  const newEntry: FieldMeta = {
    component: PMW_CUSTOM_INPUT,
    order: currentFieldsMeta.length + 1,
    label,
    format: 'string',
    customFieldIndex: newIndex,
  };
  setFieldsMeta((prev) => [...prev, newEntry]);
}
